import type { ModelSyncHeartbeatEntry } from '@avisoma/shared';
import { formatDateTime, formatRelativeTime } from '../lib/dateFormat';

// Publish/pull run daily (publish pipeline kickoff 03:00, pull 08:00), so a
// heartbeat older than ~26h means the job missed its last scheduled run — the
// one-hour margin absorbs schedule/runtime jitter without crying wolf.
const STALE_AFTER_HOURS = 26;
const STALE_AFTER_MS = STALE_AFTER_HOURS * 60 * 60 * 1000;

const JOB_LABEL: Record<string, string> = { publish: 'Publish', pull: 'Pull' };

// Display order: env ascending, then publish before pull within each env.
const JOB_ORDER: Record<string, number> = { publish: 0, pull: 1 };

const BADGE_BASE = 'inline-flex rounded px-1.5 py-0.5 text-xs font-medium';

const OUTCOME_CLASSES: Record<string, string> = {
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  error: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
};

type HashStatus = 'in-sync' | 'behind' | 'unknown';

const HASH_STATUS_LABEL: Record<HashStatus, string> = {
  'in-sync': 'In sync',
  behind: 'Behind',
  unknown: 'Unknown',
};

const HASH_STATUS_CLASSES: Record<HashStatus, string> = {
  'in-sync': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  behind: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  unknown: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-zinc-300',
};

// Env ids are 'trecs-aero-<env>'; the shared prefix is noise in a per-env table.
const shortEnv = (env: string): string => env.replace(/^trecs-aero-/, '');

const isStale = (lastRunAt: string): boolean => Date.now() - new Date(lastRunAt).getTime() > STALE_AFTER_MS;

export function ModelSyncHealth({ heartbeats }: { heartbeats: ModelSyncHeartbeatEntry[] }) {
  if (heartbeats.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        No model-sync heartbeats recorded (registry bucket not configured in this environment).
      </p>
    );
  }

  // Hash status is judged against the dev publish heartbeat — dev is the single
  // training brain, so only a publish marker from the dev env counts as the
  // source of truth (a stray publish from another env must not steal the
  // reference). Use the NEWEST *successful* one with a real hash: a transient
  // publish error must not flip every healthy row to "unknown". Without any such
  // reference, every row reads unknown.
  const referenceHash =
    heartbeats
      .filter(
        (entry) =>
          entry.job === 'publish' && shortEnv(entry.env) === 'dev' && entry.outcome === 'success' && entry.currentHash !== null,
      )
      .sort((a, b) => new Date(b.lastRunAt).getTime() - new Date(a.lastRunAt).getTime())[0]?.currentHash ?? null;
  const hashStatusOf = (entry: ModelSyncHeartbeatEntry): HashStatus => {
    // A row without a hash (e.g. an error heartbeat) has unknown sync state —
    // it is not provably behind the reference.
    if (referenceHash === null || entry.currentHash === null) return 'unknown';
    return entry.currentHash === referenceHash ? 'in-sync' : 'behind';
  };

  const rows = [...heartbeats].sort(
    (a, b) => a.env.localeCompare(b.env) || (JOB_ORDER[a.job] ?? 99) - (JOB_ORDER[b.job] ?? 99),
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-zinc-700 text-left text-xs text-gray-500 dark:text-zinc-400">
            <th className="py-1.5 pr-3 font-medium">Env</th>
            <th className="py-1.5 pr-3 font-medium">Job</th>
            <th className="py-1.5 pr-3 font-medium">Last run</th>
            <th className="py-1.5 pr-3 font-medium">Outcome</th>
            <th className="py-1.5 font-medium">Model</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => {
            const stale = isStale(entry.lastRunAt);
            const hashStatus = hashStatusOf(entry);
            return (
              <tr key={`${entry.env}-${entry.job}`} className="border-b border-gray-100 dark:border-zinc-800">
                <td className="py-1.5 pr-3 text-gray-900 dark:text-zinc-100">{shortEnv(entry.env)}</td>
                <td className="py-1.5 pr-3 text-gray-500 dark:text-zinc-400">{JOB_LABEL[entry.job] ?? entry.job}</td>
                <td className="py-1.5 pr-3">
                  <span
                    className={stale ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-zinc-400'}
                    title={formatDateTime(entry.lastRunAt)}
                  >
                    {formatRelativeTime(entry.lastRunAt)}
                    {stale ? ' · stale' : ''}
                  </span>
                </td>
                <td className="py-1.5 pr-3">
                  <span
                    className={`${BADGE_BASE} ${OUTCOME_CLASSES[entry.outcome] ?? HASH_STATUS_CLASSES.unknown}`}
                    title={entry.outcome === 'error' ? entry.errorMessage : undefined}
                  >
                    {entry.outcome === 'error' ? 'Error' : 'Success'}
                  </span>
                </td>
                <td className="py-1.5">
                  <span className={`${BADGE_BASE} ${HASH_STATUS_CLASSES[hashStatus]}`} title={entry.currentHash ?? undefined}>
                    {HASH_STATUS_LABEL[hashStatus]}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
